import React, { useEffect, useState, useRef, useLayoutEffect } from 'react';

import { Browser } from '@capacitor/browser';

import { CapacitorHttp } from '@capacitor/core';
import { useLocation } from 'react-router-dom';
import DOMPurify from 'dompurify';


import { IonButtons, IonContent, IonHeader, IonMenuButton, IonPage, IonTitle, IonToolbar, IonImg, IonIcon, IonLabel } from '@ionic/react';
import { sendOutline } from 'ionicons/icons';
import { useParams } from 'react-router';
import { logOutOutline } from 'ionicons/icons';

import './Page.css';
import '../components/Menu.css';

import { LoginData, ChatRoom, ChatMessage, UserData, getTextAvatar, calculateRoomName,  calculateAvatar } from '../Utils';

const Page: React.FC<{loginData: LoginData}> = ({loginData}) => {

  const { uid: myUid, csrfToken, wrappedCookies } = loginData;

  const { name } = useParams<{ name: string; }>();

  const [ messages, setMessages ] = useState<ChatMessage[]>([]);
  const [ roomTitle, setRoomTitle ] = useState<string>("");
  const [ users, setUsers ] = useState<UserData[]>([]);
  const [ encounteredError, setEncounteredError ] = useState<boolean>(false);
  
  const [ lastMessageTimestamp, setLastMessageTimestamp ] = useState<number>(-1);

  const location = useLocation();

  const bottomRef = useRef<HTMLDivElement>(null);

  const roomId = location.pathname.split("/").pop();
  const roomEndpoint = `https://api.browsedns.net/api/v3/chats/${roomId}`;

  useLayoutEffect(() => {
      // scroll to the bottom of the page container, every time messages change
        const timer = setTimeout(() => {
          bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100); // Adjust the delay as needed
        return () => clearTimeout(timer);
      }, [lastMessageTimestamp]
  );

  // start a timer that updates the messages every second
  // TODO: use websockets instead
  useEffect(() => {
    const interval = setInterval(() => {
      if (roomId === "about") {
        return; // don't update the messages if we're on the about page
      }
      updateMessages();
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {

    if (roomId === "about") {
      setRoomTitle("About");
      return;
    }

    // at the initial load, scroll to the bottom without animating
    bottomRef.current?.scrollIntoView({ behavior: 'auto' });
        
    updateMessages();
  }, []);

  const updateMessages = async () => {
    const options = {
      url: roomEndpoint,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": csrfToken,
        "X-Cookie": wrappedCookies
      }
    };

    CapacitorHttp.get(options)
      .then(response => {
        const data = response.data;
        if (data && data.status && data.status.code === "ok") {
          const { response: room }: { response: ChatRoom } = data;

          const lastMessageTimestamp = room.messages.length > 0 ? room.messages[room.messages.length - 1].timestamp : 0;
          setRoomTitle(calculateRoomName(room, myUid));
          setMessages(room.messages);
          setUsers(room.users);
          setLastMessageTimestamp(lastMessageTimestamp);
        }
      }).catch(() => {
        setEncounteredError(true);
      });
  };

  const sendMessage = async (message: string) => {
    const options = {
      url: roomEndpoint,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": csrfToken,
        "X-Cookie": wrappedCookies
      },
      data: {
        message
      }
    };

    const response = await CapacitorHttp.post(options);
    const { data } = response;
    if (data && data.status && data.status.code === "ok") {
      // update the messages by re-fetching
      await updateMessages();
    }
  };

  const renderLinkedUsername = (user: UserData) => {
    const profileUrl = `https://browsedns.net/user/${user.userslug}`;
    return <a href="#" onClick={
      async e => {
        e.preventDefault();
        await Browser.open({
          url: profileUrl,
          presentationStyle: "popover"
        });
      }
    }>{user.displayname}</a>;
  };

  const logout = <button
    className="big-button"
    onClick={e => {
      e.preventDefault();
      localStorage.removeItem("loginData");
      localStorage.removeItem("loginTime");
      localStorage.removeItem("loginHeaders");
      window.location.href = "/";
    }}
    >
      <IonIcon slot="start" icon={logOutOutline} />
      <IonLabel>Logout</IonLabel>
    </button>;

  let aboutBlurb = <div>
    <h2>BrowseDNS Chats</h2>
    <p>This is a simple chat app that uses the BrowseDNS Chat API.</p>
    <p>Select a chat on the left to get started.</p>
    {logout}
  </div>;

  const makeChatLine = (message: ChatMessage, index: number) => {
    const { content, timestamp, fromUser: user, newSet } = message;

    if (content == "user-join") {
      return <div key={index} className="status-message">
          <strong>{renderLinkedUsername(user)}</strong> joined the chat.
      </div>;
    } else if (content == "user-leave") {
      return <div key={index} className="status-message">
        <strong>{renderLinkedUsername(user)}</strong> left the chat.
      </div>;
    }

    const sanitizedContent = DOMPurify.sanitize(content);

    const avatar = calculateAvatar(user);

    const formattedDate = (() => {
      const date = new Date(timestamp);
      return `${date.getMonth()+1}/${date.getDate()}/${date.getFullYear()} ${date.getHours()}:${date.getMinutes().toString().padStart(2, '0')}`;
    })();

    let senderHeader = <div className="chat-header">
      {avatar}
      <strong>{renderLinkedUsername(user)}</strong>
      <span className="timestamp">{formattedDate}</span>
    </div>;

    // if this message isn't marked newSet, hide the sender header
    if (!newSet) {
      senderHeader = <></>;
    }

    return <div key={index} className="chat-line">
      <div className="chat-content">
        {senderHeader}
        <div className="chat-message" dangerouslySetInnerHTML={{ __html: sanitizedContent }} />
      </div>
    </div>;
  };

  const renderUsersPreview = (users: UserData[]) => {
    // first 6 users
    const resp = users.slice(0, 6).map(user => calculateAvatar(user));
    // if more than 6, add a "+n" avatar
    if (users.length > 6) {
      resp.push(<IonImg className="menu-avatar" key="more" src={getTextAvatar("darkgray", `+${users.length - 6}`)} />);
    }
    return resp;
  }

  if (encounteredError) {
    aboutBlurb = <div>
      <h2>Error Loading Chats</h2>
      <p>There was an error loading chat messages. Are you offline?</p>
      <p>Please try again later.</p>
      {logout}
    </div>;
  }

  const sendButtonCallback = async () => {
    const textField = (document.querySelector("input[name=textMessage]") as HTMLInputElement);
    const message = textField.value;
    try {
      await sendMessage(message);
      textField.value = "";
    } catch (e) {
      alert("Failed to send message: " + e);
    }
  };

  let inputField = <div className="input-field">
    <input
      type="text" placeholder="Type a message..."
      autoComplete="off" name="textMessage"
      onKeyDown={e => {
        if (e.key === "Enter") {
          e.preventDefault();
          sendButtonCallback();
        }
      }}
    />
    <button onClick={sendButtonCallback}>
      <IonIcon src={sendOutline} />
    </button>
  </div>;

  if (name === "about" || encounteredError) {
    inputField = <></>;
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonButtons collapse={true} slot="end">
            <div className="chat-current-users">
              {renderUsersPreview(users)}
            </div>
          </IonButtons>
          <IonTitle>
            <span className="chat-title">{roomTitle}</span>
          </IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <IonHeader collapse="condense">
          <IonToolbar>
            <IonTitle size="large">{roomTitle}</IonTitle>
          </IonToolbar>
        </IonHeader>
        <div className="container">
          {name === "about" || encounteredError ? aboutBlurb : messages.map((message, index) => {
            return makeChatLine(message, index);
          })}
          <div id="bottom-chat-ref" ref={bottomRef}></div>
        </div>
        {inputField}
      </IonContent>
    </IonPage>
  );
};

export default Page;
